import { useCallback, useState } from 'react';
import { Input } from '../../atoms/input';
import { FormField } from '@design-system/molecules/form';
import { useFormState } from 'react-hook-form';
import { useCapsLockDetection } from '@design-system/hooks/useCapsLockDetection';
import { PasswordFieldProps } from '@design-system/molecules/password-field/password-field.types';

/**
 * PasswordField component
 * @param props - PasswordFieldProps
 * @returns PasswordField component
 */
export const PasswordField = (props: PasswordFieldProps) => {
  const {
    name,
    label,
    placeholder,
    hintConfig: { useErrorAsHint = false, capslockOn },
    ...inputProps
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const isCapsLockOn = useCapsLockDetection();
  const { errors } = useFormState();
  const [isFocused, setIsFocused] = useState(false);

  const getHint = useCallback(() => {
    if (!isFocused) {
      return undefined;
    }

    if (isCapsLockOn) {
      return capslockOn;
    }

    if (useErrorAsHint && errors[name]?.message) {
      return errors[name]?.message;
    }
  }, [isCapsLockOn, useErrorAsHint, capslockOn, errors, name, isFocused]);

  return (
    <FormField name={name}>
      {({ ...field }) => {
        return (
          <Input
            {...field}
            {...inputProps}
            label={label}
            type={showPassword ? 'text' : 'password'}
            placeholder={placeholder}
            // @ts-expect-error
            hint={getHint()}
            // @ts-expect-error
            error={errors[name]?.message}
            onPressEndIcon={() => setShowPassword((prev) => !prev)}
            endIcon={showPassword ? 'eye' : 'eyeClose'}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        );
      }}
    </FormField>
  );
};

PasswordField.displayName = 'PasswordField';
